<script setup lang="ts">
  import { computed } from 'vue';

  import type { UserListItem } from '@stuut/api-sdk/models';
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
  } from '@ui/components/dialog';
  import { Skeleton } from '@ui/components/skeleton';

  import OutOfOfficeResponderForm from '@/components/out-of-office/OutOfOfficeResponderForm.vue';
  import { getUserOutOfOfficeState } from '@/components/out-of-office/useOutOfOfficeResponderForm';
  import { useOutOfOfficeQuery } from '@/composables/useUserOutOfOffice';

  const props = defineProps<{
    user: UserListItem;
  }>();

  const emit = defineEmits<{
    saved: [user: UserListItem];
  }>();

  const open = defineModel<boolean>('open', { required: true });

  const oooQuery = useOutOfOfficeQuery(() => props.user.id, {
    enabled: () => open.value,
  });

  const activeOutOfOffice = computed(() => oooQuery.data.value ?? null);
  const isLoadingActiveOutOfOffice = computed(() => open.value && oooQuery.isLoading.value);

  const userOutOfOfficeState = computed(() => getUserOutOfOfficeState(props.user));
  const backupUserId = computed(
    () => activeOutOfOffice.value?.backupUserId ?? userOutOfOfficeState.value.backupUserId,
  );
  const firstDay = computed(() => activeOutOfOffice.value?.startDate);
  const lastDay = computed(() => activeOutOfOffice.value?.endDate);
  const hasExistingOutOfOffice = computed(
    () => !!activeOutOfOffice.value || userOutOfOfficeState.value.isOutOfOffice,
  );

  const closeDialog = () => {
    open.value = false;
  };

  const onSaved = () => {
    emit('saved', props.user);
    closeDialog();
  };
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="max-h-[min(90vh,720px)] w-full max-w-2xl gap-0 overflow-y-auto p-0">
      <DialogHeader class="border-b border-border px-6 py-4 text-left">
        <DialogTitle>Out of Office Task Auto Assigner</DialogTitle>
        <DialogDescription>
          {{ props.user.name }}
          <span v-if="props.user.email" class="text-muted-foreground">
            · {{ props.user.email }}</span
          >
        </DialogDescription>
      </DialogHeader>

      <div v-if="isLoadingActiveOutOfOffice" class="flex flex-col gap-6 px-6 py-6">
        <div class="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div v-for="i in 3" :key="i" class="flex flex-col gap-3">
            <Skeleton class="h-4 w-24" />
            <Skeleton class="h-9 w-full" />
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <Skeleton class="h-9 w-24" />
          <Skeleton class="h-9 w-20" />
        </div>
      </div>
      <OutOfOfficeResponderForm
        v-else
        class="px-6 py-6"
        :target-user-id="props.user.id"
        :backup-user-id="backupUserId"
        :first-day="firstDay"
        :has-existing-out-of-office="hasExistingOutOfOffice"
        :last-day="lastDay"
        @saved="onSaved"
        @cancelled="closeDialog"
      />
    </DialogContent>
  </Dialog>
</template>
